import React, { forwardRef, useId, useState, useEffect } from "react";
import clsx from "clsx";
import { Span } from "@/components/ui";

export type ToggleProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'onChange'
> & {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  wrapperClassName?: string;
  /** Left glyph (e.g. sun). With `trailingSlot`, both render inside the track and the knob hides one at a time. */
  leadingSlot?: React.ReactNode;
  /** Right glyph (e.g. moon). With `leadingSlot`, both render inside the track and the knob hides one at a time. */
  trailingSlot?: React.ReactNode;
  focusRing?: boolean;
};

type SizeKey = 'sm' | 'md' | 'lg';

/** Plain switch — knob slides in-track; `px-1` inset so the off (left) position is not flush to the track edge. */
const sizeMap: Record<SizeKey, { track: string; knob: string; knobTranslate: string }> = {
  sm: {
    track: 'h-5 w-9 px-1',
    knob: 'bg-glass relative z-[1] inline-block h-4 w-4 translate-x-0 rounded-full bg-white shadow transition-transform duration-200 ease-out',
    knobTranslate: 'translate-x-3',
  },
  md: {
    track: 'h-7 w-12 px-1',
    knob: 'bg-glass relative z-[1] inline-block h-6 w-6 translate-x-0 rounded-full bg-white shadow transition-transform duration-200 ease-out',
    knobTranslate: 'translate-x-4',
  },
  lg: {
    track: 'h-9 w-16 px-1',
    knob: 'bg-glass relative z-[1] inline-block h-8 w-8 translate-x-0 rounded-full bg-white shadow transition-transform duration-200 ease-out',
    knobTranslate: 'translate-x-6',
  },
};

/**
 * Two icons + sliding pill: knob covers the inactive side so only one glyph reads at a time.
 * `checked` → knob on the left (covers `leadingSlot`); unchecked → knob on the right (covers `trailingSlot`).
 */
const duoMap: Record<
  SizeKey,
  { track: string; knob: string; knobShift: string; iconBox: string }
> = {
  sm: {
    track:
      'h-6 w-[3.625rem] shrink-0 rounded-full border border-[color-mix(in_srgb,var(--semantic-border-default)_35%,transparent)] bg-[color-mix(in_srgb,var(--semantic-text-primary)_6%,transparent)] p-px shadow-inner dark:border-white/10 dark:bg-white/5',
    knob:
      'absolute left-0.5 top-0.5 z-[2] h-[calc(100%-4px)] w-[calc(50%-3px)] rounded-full bg-white shadow-md ring-1 ring-black/[0.06] transition-transform duration-200 ease-out dark:bg-[color-mix(in_srgb,var(--semantic-bg-secondary)_96%,transparent)] dark:ring-white/12',
    knobShift: 'translate-x-[1.75rem]',
    iconBox: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  },
  md: {
    track:
      'h-8 w-[4.75rem] shrink-0 rounded-full border border-[color-mix(in_srgb,var(--semantic-border-default)_35%,transparent)] bg-[color-mix(in_srgb,var(--semantic-text-primary)_6%,transparent)] p-px shadow-inner dark:border-white/10 dark:bg-white/5',
    knob:
      'absolute left-0.5 top-0.5 z-[2] h-[calc(100%-4px)] w-[calc(50%-3px)] rounded-full bg-white shadow-md ring-1 ring-black/[0.06] transition-transform duration-200 ease-out dark:bg-[color-mix(in_srgb,var(--semantic-bg-secondary)_96%,transparent)] dark:ring-white/12',
    knobShift: 'translate-x-[2.375rem]',
    iconBox: '[&_svg]:h-4 [&_svg]:w-4',
  },
  lg: {
    track:
      'h-9 w-24 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--semantic-border-default)_35%,transparent)] bg-[color-mix(in_srgb,var(--semantic-text-primary)_6%,transparent)] p-px shadow-inner dark:border-white/10 dark:bg-white/5',
    knob:
      'absolute left-0.5 top-0.5 z-[2] h-[calc(100%-4px)] w-[calc(50%-3px)] rounded-full bg-white shadow-md ring-1 ring-black/[0.06] transition-transform duration-200 ease-out dark:bg-[color-mix(in_srgb,var(--semantic-bg-secondary)_96%,transparent)] dark:ring-white/12',
    knobShift: 'translate-x-[3rem]',
    iconBox: '[&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]',
  },
};

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>((props, ref) => {
  const {
    checked: controlledChecked,
    defaultChecked = false,
    onChange,
    size = 'md',
    label,
    description,
    disabled = false,
    wrapperClassName,
    leadingSlot,
    trailingSlot,
    className,
    focusRing = true,
    ...rest
  } = props;

  const id = useId();
  const isControlled = typeof controlledChecked === 'boolean';
  const [internalChecked, setInternalChecked] = useState<boolean>(defaultChecked);
  const checked = isControlled ? (controlledChecked as boolean) : internalChecked;

  const hasDuoIcons = Boolean(leadingSlot && trailingSlot);
  const s = sizeMap[size];
  const d = duoMap[size];

  useEffect(() => {
    if (isControlled) setInternalChecked(controlledChecked as boolean);
  }, [controlledChecked, isControlled]);

  function toggle(e?: React.MouseEvent<HTMLButtonElement>) {
    if (disabled) return;
    const next = !checked;
    if (!isControlled) setInternalChecked(next);
    onChange?.(next);
    e?.preventDefault();
  }

  if (hasDuoIcons) {
    return (
      <div className={clsx('inline-flex items-center gap-3', wrapperClassName)}>
        <button
          {...rest}
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label ? undefined : 'Toggle switch'}
          aria-labelledby={label ? `${id}-label` : undefined}
          aria-describedby={description ? `${id}-desc` : undefined}
          disabled={disabled}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
          className={clsx(
            'relative inline-flex shrink-0 overflow-hidden transition-colors duration-200 focus:outline-none',
            d.track,
            checked ? 'dark:bg-primary-500/25' : '',
            disabled && 'cursor-not-allowed opacity-60',
            focusRing &&
              !disabled &&
              'focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2',
            className,
          )}
        >
          <div
            className={clsx(
              'pointer-events-none absolute inset-px flex items-stretch text-current',
              d.iconBox,
            )}
            aria-hidden
          >
            <span className="flex flex-1 items-center justify-center opacity-90">{leadingSlot}</span>
            <span className="flex flex-1 items-center justify-center opacity-90">{trailingSlot}</span>
          </div>
          <Span
            aria-hidden
            className={clsx(d.knob, checked ? 'translate-x-0' : d.knobShift)}
          />
        </button>
        {(label || description) && (
          <div className="hidden">
            {label && (
              <span id={`${id}-label`} className="font-medium text-gray-900 dark:text-gray-100">
                {label}
              </span>
            )}
            {description && (
              <span id={`${id}-desc`} className="text-xs text-gray-500 dark:text-gray-400">
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'inline-flex items-center',
        leadingSlot || trailingSlot ? 'gap-2' : 'gap-3',
        wrapperClassName,
      )}
    >
      <button
        {...rest}
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label ? undefined : 'Toggle switch'}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-describedby={description ? `${id}-desc` : undefined}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        className={clsx(
          'bg-glass relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none',
          s.track,
          checked ? 'bg-gray-200 dark:bg-primary-500' : 'bg-gray-200 dark:bg-gray-700',
          disabled && 'cursor-not-allowed opacity-60',
          focusRing &&
            !disabled &&
            'focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2',
          className,
        )}
      >
        {trailingSlot ? (
          <span className="inline-flex shrink-0 text-current" aria-hidden>
            {trailingSlot}
          </span>
        ) : null}
        <Span aria-hidden className={clsx(s.knob, checked && s.knobTranslate)} />
        {leadingSlot ? (
          <span className="inline-flex shrink-0 text-current" aria-hidden>
            {leadingSlot}
          </span>
        ) : null}
      </button>

      {(label || description) && (
        <div className="hidden">
          {label && (
            <span id={`${id}-label`} className="font-medium text-gray-900 dark:text-gray-100">
              {label}
            </span>
          )}
          {description && (
            <span id={`${id}-desc`} className="text-xs text-gray-500 dark:text-gray-400">
              {description}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

Toggle.displayName = 'Toggle';

export default Toggle;
